import {
  doc, collection, getDoc, getDocs, setDoc, deleteDoc,
  onSnapshot, query, where, orderBy, limit, getCountFromServer,
  Timestamp, type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import type { ActiveBot, BotConfig, PublicMessage } from '../types';

const BOT_CONFIG_DOC = doc(db, 'botConfig', 'main');

// ─── Bot Config ───

const DEFAULT_CONFIG: BotConfig = {
  enabled: false,
  openaiApiKey: '',
  googleMapsApiKey: '',
  useGeocodingAPI: false,
  gptModel: 'gpt-4o-mini',
  maxTokens: 60,
  temperature: 0.9,
  userMessageResponseProbability: 0.6,
  botConversationProbability: 0.2,
  additionalResponseProbability: 0.3,
  activityDurationMin: 30,
  activityDurationMax: 120,
  goodbyeProbability: 0.5,
  responseDelayMin: 3,
  responseDelayMax: 15,
  responseDistanceMin: 100,
  responseDistanceMax: 800,
  maxBotsPerArea: 5,
  botLimitRadiusKm: 1.0,
  maxResponsesPerUser: 3,
  minBotResponseInterval: 15,
  maxConversationChainDepth: 3,
  maxBotMessagesPerWindow: 10,
  botMessageWindowSeconds: 60,
  conversationHistoryCount: 5,
  conversationHistoryDistance: 800,
  autoConversationEnabled: false,
  autoConversationIntervalMin: 60,
  autoConversationIntervalMax: 300,
  autoConversationProbability: 0.3,
  ambientBotsEnabled: false,
  ambientBotsMax: 10,
  ambientBotsPerCycle: 3,
  ambientSpawnIntervalMinutes: 10,
  botProfileImageProbability: 0.5,
  botBioProbability: 0.5,
  botBioEmojiProbability: 0.2,
  botChatEmojiProbability: 0.3,
  botDmReplyEnabled: false,
  botDmReplyDelayMin: 30,
  botDmReplyDelayMax: 300,
  cleanupBotMessagesAfterDays: 0,
  systemPromptTemplate: '',
  goodbyePromptTemplate: '',
  additionalResponsePromptTemplate: '',
  autoConversationPromptTemplate: '',
};

export async function getBotConfig(): Promise<BotConfig> {
  const snap = await getDoc(BOT_CONFIG_DOC);
  if (!snap.exists()) return { ...DEFAULT_CONFIG };
  return { ...DEFAULT_CONFIG, ...snap.data() } as BotConfig;
}

export function subscribeBotConfig(onUpdate: (cfg: BotConfig) => void): Unsubscribe {
  return onSnapshot(BOT_CONFIG_DOC, (snap) => {
    if (snap.exists()) {
      onUpdate({ ...DEFAULT_CONFIG, ...snap.data() } as BotConfig);
    } else {
      onUpdate({ ...DEFAULT_CONFIG });
    }
  });
}

export async function updateBotConfig(updates: Partial<BotConfig>): Promise<void> {
  await setDoc(BOT_CONFIG_DOC, {
    ...updates,
    updatedAt: Timestamp.now(),
  }, { merge: true });
}

// ─── Active Bots ───

export async function getActiveBots(): Promise<ActiveBot[]> {
  const now = Timestamp.now();
  const q = query(
    collection(db, 'activeBots'),
    where('expiresAt', '>', now),
    orderBy('expiresAt', 'desc'),
    limit(100),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as ActiveBot);
}

export function subscribeActiveBots(onUpdate: (bots: ActiveBot[]) => void): Unsubscribe {
  const now = Timestamp.now();
  const q = query(
    collection(db, 'activeBots'),
    where('expiresAt', '>', now),
    orderBy('expiresAt', 'desc'),
    limit(100),
  );
  return onSnapshot(q, (snap) => {
    onUpdate(snap.docs.map((d) => d.data() as ActiveBot));
  });
}

export async function getAllActiveBots(): Promise<ActiveBot[]> {
  const snap = await getDocs(collection(db, 'activeBots'));
  return snap.docs.map((d) => d.data() as ActiveBot);
}

export async function deleteActiveBot(uid: string): Promise<void> {
  // Delete active bot doc
  await deleteDoc(doc(db, 'activeBots', uid));
  // Delete user doc
  await deleteDoc(doc(db, 'users', uid));
}

export async function deleteExpiredBots(): Promise<number> {
  const now = Timestamp.now();
  const snap = await getDocs(collection(db, 'activeBots'));
  let count = 0;
  for (const d of snap.docs) {
    const bot = d.data() as ActiveBot;
    const expiresAt = bot.expiresAt;
    if (expiresAt && expiresAt.toMillis() <= now.toMillis()) {
      await deleteDoc(doc(db, 'activeBots', bot.uid));
      await deleteDoc(doc(db, 'users', bot.uid));
      count++;
    }
  }
  return count;
}

export async function deleteAllActiveBots(): Promise<void> {
  const snap = await getDocs(collection(db, 'activeBots'));
  for (const d of snap.docs) {
    const bot = d.data() as ActiveBot;
    await deleteDoc(doc(db, 'activeBots', bot.uid));
    await deleteDoc(doc(db, 'users', bot.uid));
  }
}

// ─── Bot Stats ───

const BOT_STATS_DOC = doc(db, 'botConfig', 'stats');

export interface BotStats {
  activeBotCount: number;
  /** Bot messages counted since the last stats reset (never negative). */
  totalBotMessages: number;
  /** Raw count of all bot messages in Firestore, ignoring the reset baseline. */
  totalBotMessagesRaw: number;
  resetAt: Timestamp | null;
}

async function getMessageCountBaseline(): Promise<{ baseline: number; resetAt: Timestamp | null }> {
  const snap = await getDoc(BOT_STATS_DOC);
  const data = snap.data();
  return {
    baseline: typeof data?.messageCountBaseline === 'number' ? data.messageCountBaseline : 0,
    resetAt: (data?.resetAt as Timestamp) ?? null,
  };
}

async function countBotMessages(): Promise<number> {
  const msgQ = query(
    collection(db, 'messages'),
    where('isBot', '==', true),
  );
  const msgSnap = await getCountFromServer(msgQ);
  return msgSnap.data().count;
}

export async function getBotStats(): Promise<BotStats> {
  const now = Timestamp.now();
  const activeQ = query(
    collection(db, 'activeBots'),
    where('expiresAt', '>', now),
  );
  const activeSnap = await getDocs(activeQ);

  const rawCount = await countBotMessages();
  const { baseline, resetAt } = await getMessageCountBaseline();

  return {
    activeBotCount: activeSnap.size,
    // botMessageCleanup can delete old messages, pushing the raw count below
    // the baseline — clamp so the card never shows a negative number.
    totalBotMessages: Math.max(0, rawCount - baseline),
    totalBotMessagesRaw: rawCount,
    resetAt,
  };
}

/**
 * Reset the bot message / cost counters shown in the monitoring tab.
 * Records the current message count as the new baseline — no messages are deleted.
 */
export async function resetBotStats(resetBy: string): Promise<void> {
  const rawCount = await countBotMessages();
  await setDoc(
    BOT_STATS_DOC,
    { messageCountBaseline: rawCount, resetAt: Timestamp.now(), resetBy },
    { merge: true },
  );
}

// ─── Bot Conversation Log ───

/**
 * Get messages near a bot's location within its lifetime.
 * Returns both bot's own messages and nearby user messages for context.
 */
export async function getBotConversationLog(bot: ActiveBot): Promise<PublicMessage[]> {
  const q = query(
    collection(db, 'messages'),
    orderBy('timestamp', 'desc'),
    limit(100),
  );
  const snap = await getDocs(q);

  const createdMs = bot.createdAt?.toDate?.()?.getTime?.() ?? 0;
  const allMessages = snap.docs
    .map((d) => ({ id: d.id, ...d.data() } as PublicMessage))
    .filter((m) => {
      // Filter by time: only messages since bot was created
      const msgTime = m.timestamp?.toDate?.()?.getTime?.() ?? 0;
      if (msgTime < createdMs) return false;
      // Filter by proximity: messages from bot itself OR nearby
      if (m.senderId === bot.uid) return true;
      if (m.lat == null || m.lng == null) return false;
      const dist = haversineDistance(bot.lat, bot.lng, m.lat, m.lng);
      return dist <= 1000; // 1km radius
    })
    .reverse(); // chronological order

  return allMessages;
}

function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
